import { h } from 'preact';
import {
	useContext, createContext, useMemo, useState,
} from 'preact/compat';
import { useVGS } from './vgs';
import { useUser } from './user';
import { useDonationStep, DonationStepEnum } from './step';
import { Theme } from '../lib/render';
import { useCart, normalizeCurrency } from './cart';
import { validConfirmTransition } from './transitionGaurds';

type SubmitContextType = {
	submit: () => void;
	submitAttempted: boolean;
	errorMessage: string;
	isSubmitting: boolean;
}

const SubmitContext = createContext<SubmitContextType | undefined>(undefined);

const SubmitProvider = ({ children }: { children: h.JSX.Element }) => {
	const theme = useContext(Theme);
	const step = useDonationStep();
	const { cart } = useCart();
	const {
		user,
		setErrors,
		dispatchTouch,
	} = useUser();
	const {
		dispatchVgs,
		vgsInit,
	} = useVGS();
	const submitAllowed = validConfirmTransition();

	/**
	 * Internal state.
	 */
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitAttempted, setSubmitAttempted] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	/**
	 * Handle Errors.
	 * Submission errors are not always a consistent data object.
	 */
	const handleErrors = useMemo(() => (errors: {
		errors?: string;
		json?: {
			errors?: {
				key?: string;
			}[];
		};
		[key: string]: any;
	}) => {
		// Error type.
		let delegate;
		if (errors) {
			delegate = errors.error ? errors.error.toLowerCase() : 'uncaught';
		} else {
			delegate = 'badresponse';
		}

		const msg = {
			unknown: 'We were unable to process your transaction at this time. Please double-check the information you submitted and press the button to try your donation again.',
			submission: 'Please verify your information and press the button to try your donation again.',
			disallowed: 'There was a problem submitting your transaction. Please verify your information and try again.',
			vgs: 'There was a problem completing your transaction. Please verify credit card information and try again.',
			request: 'There was a problem processing your transaction. Please try again later.',
			invalid: 'There was a problem handling your transaction. Please try again later.',
			badresponse: 'There was a problem running your transaction. Please try again later.',
			gatewayError: 'There was a problem running your transaction. Please try again later.',
		};

		// Step target.
		let stepTarget: false | DonationStepEnum = false;

		console.error('errors', errors);
		/**
		 * Delegate errors.
		 */
		switch (delegate) {
		case 'submission':
			if (
				errors.json.errors
				&& Array.isArray(errors.json.errors)
				&& errors.json.errors.length > 0
			) {
				/**
				 * Handle gateway id errors.
				 * EA responses are not consistent, always check if prop exists on obj.
				 */
				if (errors.json.errors.some((error) => error?.key && error.key.includes('gateway IDs are not configured'))) {
					setErrorMessage(msg.gatewayError);
					break;
				}

				/**
				 * Default submission error.
				 * Parse form errors to key/value pairs.
				 * Then pass errors to user context + move user to checkout form.
				 */
				const errProps: {
					[key: string]: string;
				} = {};
				errors.json.errors.forEach((e: any) => {
					if (e.properties) {
						e.properties.forEach((p: any) => {
							errProps[p] = 'Confirm value';
						});
					}
				});
				setErrors(errProps);
				stepTarget = DonationStepEnum.CHECKOUT;
				setErrorMessage(msg.submission);
			} else {
				stepTarget = DonationStepEnum.CHECKOUT;
				setErrorMessage(msg.unknown);
			}
			break;
		case 'disallowed_data':
			stepTarget = DonationStepEnum.CHECKOUT;
			setErrorMessage(msg.disallowed);
			break;
		case 'vgs':
			stepTarget = DonationStepEnum.CHECKOUT;
			setErrorMessage(msg.vgs);
			break;
		case 'request':
			setErrorMessage(msg.request);
			break;
		case 'invalid_input':
			setErrorMessage(msg.invalid);
			break;
		case 'badresponse':
			setErrorMessage(msg.badresponse);
			break;
		case 'uncaught':
		default:
			setErrorMessage(msg.badresponse);
			break;
		}

		/**
		 * Go to step if applicable.
		 */
		if (stepTarget) {
			/**
			 * Trigger full touch.
			 * This is necessary to trigger the error message.
			 * Return to checkout form inputs.
			 */
			dispatchTouch({
				type: 'TOUCH_ALL',
			});
			dispatchVgs({
				type: 'TOUCH_ALL',
			});
			step.dispatch({
				type: 'MANUAL_STEP',
				payload: {
					step: DonationStepEnum.CHECKOUT,
				},
			});
		}
	}, []);

	/**
	 * Handle Success.
	 */
	const handleSuccess = useMemo(() => (response: any) => {
		/**
		 * Success exits flow.
		 */
		step.dispatch({
			type: 'MANUAL_STEP',
			payload: {
				step: DonationStepEnum.SUCCESS,
			},
		});
	}, []);

	/**
	 * Submit Function.
	 */
	const submit = useMemo(() => () => {
		setIsSubmitting(true);
		setSubmitAttempted(true);

		/**
		 * Validate all states.
		 * We default to disallowed data error,
		 * will trigger navigation to checkout.
		 */
		if (!submitAllowed) {
			handleErrors({
				error: 'disallowed_data',
			});
			return;
		}

		// Format split amounts from cart.
		const split: {
				[key: number]: {
					am: number;
				};
			} = {};
		Array.from(cart.selections.values()).forEach((c) => {
			split[c.gateway_id] = {
				// Ensure valid amount.
				am: normalizeCurrency(c.amount).value,
			};
		});

		const {
			FirstName,
			LastName,
			AddressLine1,
			AddressLine2,
			City,
			StateProvince,
			PostalCode,
			EmailAddress,
			Occupation,
			Employer,
			sourceCodeName,
			sourceCodeId,
		} = user;

		/**
		 * Construct API data.
		 */
		const submission = {
			FirstName,
			LastName,
			AddressLine1,
			AddressLine2,
			City,
			StateProvince,
			PostalCode,
			EmailAddress,
			Occupation,
			Employer,
			SplitAmounts: split,
		};

		/**
		 * sourceCodeId takes precedence over sourceCodeName.
		 * Except on 'caging' forms.
		 */
		if (theme.donate_type !== 'caging' && sourceCodeId) {
			// @ts-ignore
			submission.sourceCodeId = sourceCodeId;
		} else if (sourceCodeName) {
			// @ts-ignore
			submission.sourceCodeName = sourceCodeName;
		}

		// Submit via vgsInit.
		vgsInit
			.submit(theme.ea_form_id, submission)
			.then((res: any) => {
				handleSuccess(res);
			})
			.catch((err: any) => {
				handleErrors(err);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	}, [cart, user, theme, submitAllowed]);

	/**
	 * Context Values.
	 */
	const contextValue = useMemo(() => ({
		submit,
		submitAttempted,
		errorMessage,
		isSubmitting,
	}), [submit, submitAttempted, errorMessage, isSubmitting]);

	return (
		<SubmitContext.Provider value={contextValue}>
			{children}
		</SubmitContext.Provider>
	);
};

const useSubmit = () => {
	const context = useContext(SubmitContext);
	if (context === undefined) {
		throw new Error('useSubmit must be used within a SubmitProvider');
	}
	return context;
};

export { SubmitProvider, useSubmit };
